
# minimum limit for axioms in a system?

> If we have an axiom system with a finite number of axioms, we can always reduce them to only one, replacing the set of original axioms with their conjunction.
>
> Thus, every non-trivial axiom system that is finitely axiomatized can be formulated in an equivalent form with a single axiom.
>
> Gödel's Incompleteness Theorems apply to systems that (in addition to other conditions) have a set of axioms that is finite or at least decidable; Robinson arithmetic, for example, is finitely axiomatized and it is enough for G's Theorem.

from [logic - What is the minimum number of axioms required for a system of axioms? - Philosophy Stack Exchange](https://philosophy.stackexchange.com/questions/22203/what-is-the-minimum-number-of-axioms-required-for-a-system-of-axioms)

# Robinson arithmetic

**1. Sx ≠ 0**

0 is not the successor of any number.

**2. (Sx = Sy) → x = y**

If the successor of x is identical to the successor of y, then x and y are identical. (1) and (2) yield the minimum of facts about N (it is an infinite set bounded by 0) and S (it is an injective function whose domain is N) needed for non-triviality. The converse of (2) follows from the properties of identity.

**3. y=0 ∨ ∃x (Sx = y)**

Every number is either 0 or the successor of some number. The axiom schema of mathematical induction present in arithmetics stronger than Q turns this axiom into a theorem.

**4. x + 0 = x**

**5. x + Sy = S(x + y)**

(4) and (5) are the recursive definition of addition.

**6. x·0 = 0**

**7. x·Sy = (x·y) + x**

(6) and (7) are the recursive definition of multiplication.

# [Travelling salesman problem - Wikipedia](https://en.wikipedia.org/wiki/Travelling_salesman_problem)

- and cognitive psychology https://en.wikipedia.org/wiki/Travelling_salesman_problem#Human_performance
- [Geometric measure theory - Wikipedia](https://en.wikipedia.org/wiki/Geometric_measure_theory)

# [General number field sieve - Wikipedia](https://en.wikipedia.org/wiki/General_number_field_sieve)

- [Lenstra's elliptic curve factorization method - YouTube](https://www.youtube.com/watch?v=p5KR2CUeEuk)

# links, notes and resources

- [Peano axioms - Wikipedia](https://en.wikipedia.org/wiki/Peano_axioms#First-order_theory_of_arithmetic)
- [Cardinality - Wikipedia](https://en.wikipedia.org/wiki/Cardinality#Comparing_sets)
- [Zermelo–Fraenkel set theory - Wikipedia](https://en.wikipedia.org/wiki/Zermelo%E2%80%93Fraenkel_set_theory)
- [Hilbert's paradox of the Grand Hotel - Wikipedia](https://en.wikipedia.org/wiki/Hilbert%27s_paradox_of_the_Grand_Hotel)
- [Galileo's paradox - Wikipedia](https://en.wikipedia.org/wiki/Galileo%27s_paradox)
- [Pigeonhole principle - Wikipedia](https://en.wikipedia.org/wiki/Pigeonhole_principle)
- [Public-key based on roots of polynomial - Cryptography Stack Exchange](https://crypto.stackexchange.com/questions/15094/public-key-based-on-roots-of-polynomial)
- [Chapter 1 The Gödel Phenomena in Mathematics - IAS School of ...  www.math.ias.edu › ~avi › BOOKS › Godel_Widgerson_Text](http://www.math.ias.edu/~avi/BOOKS/Godel_Widgerson_Text.pdf)
- [Gödel numbering - Wikipedia](https://en.wikipedia.org/wiki/G%C3%B6del_numbering)
- [Mathematics, Cryptology, Security.](http://www.di.ens.fr/pub/Main/JS60/14-Stern.pdf)
- [Crypto Aspects of The Jacobian Conjecture | Gödel's Lost Letter and P=NP](https://rjlipton.wordpress.com/2013/08/04/crypto-aspects-of-the-jacobian-conjecture/)
- [The Gödel Letter | Gödel's Lost Letter and P=NP](https://rjlipton.wordpress.com/the-gdel-letter/)
- [P versus NP problem - Wikipedia](https://en.wikipedia.org/wiki/P_versus_NP_problem)
  - "An answer to the P = NP question would determine whether problems that can be verified in polynomial time can also be solved in polynomial time."
